/**
 * Long-form article for blog.healthy-pet.app.
 * Separate from the card slides used in the apps.
 */

import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../app/firebase";
import {
  Div,
  FeedbackFormInput,
  FeedbackFormTextarea,
  P,
  Button,
} from "../../../styles/styles";
import {
  deleteStoragePath,
  isValidArticleImageUrl,
  uploadArticleImage,
} from "../../../utils/uploadArticleImage";
import { emptySiteForm, siteFormFromDoc } from "../../../utils/siteArticle";
import AdminArticleSiteEditor from "./adminArticleSiteEditor";

export default function AdminArticleSiteSection({
  formData,
  setFormData,
  articleFirestoreKey,
  loadExisting,
  onReadyChange,
}) {
  const [siteReady, setSiteReady] = useState(!loadExisting);
  const [loadError, setLoadError] = useState("");
  const [ogUploading, setOgUploading] = useState(false);
  const site = formData.site || emptySiteForm();
  const slug = String(formData.slug?.en || "").trim();

  useEffect(() => {
    if (!loadExisting || !articleFirestoreKey || !db) {
      setSiteReady(true);
      return undefined;
    }
    let cancelled = false;
    setSiteReady(false);
    setLoadError("");
    getDoc(doc(db, "articles", articleFirestoreKey, "web", "content"))
      .then((snap) => {
        if (cancelled) return;
        if (snap.exists()) {
          const loaded = siteFormFromDoc(snap.data());
          setFormData((prev) => ({ ...prev, site: loaded }));
        }
        setSiteReady(true);
      })
      .catch((err) => {
        console.warn("Failed to load site article", err);
        if (!cancelled) {
          setLoadError("Could not load the site article. Refresh before saving.");
          setSiteReady(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [articleFirestoreKey, loadExisting, setFormData]);

  useEffect(() => {
    onReadyChange?.(siteReady);
  }, [onReadyChange, siteReady]);

  const patchSite = (partial) => {
    setFormData((prev) => ({
      ...prev,
      site: { ...emptySiteForm(), ...(prev.site || {}), ...partial },
    }));
  };

  const onOgPick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !articleFirestoreKey) return;
    setOgUploading(true);
    try {
      const url = await uploadArticleImage({
        articleId: articleFirestoreKey,
        kind: "inline",
        imageId: "og",
        file,
      });
      patchSite({ ogImageUrl: url });
    } catch (err) {
      window.alert(err?.message || "OG image upload failed");
    } finally {
      setOgUploading(false);
    }
  };

  const onOgClear = async () => {
    if (articleFirestoreKey) {
      try {
        await deleteStoragePath(`articles/${articleFirestoreKey}/web/og.jpg`);
      } catch (err) {
        window.alert(err?.message || "Failed to remove OG image");
        return;
      }
    }
    patchSite({ ogImageUrl: "" });
  };

  return (
    <Div
      width="100%"
      gap="12px"
      alignStart
      padding="16px"
      borderRadius="10px"
      style={{ border: "1px solid #d9d9d9", boxSizing: "border-box" }}
    >
      <P fontWeight="600" align="left" margin="0" textWidth="auto">
        Site article
      </P>
      <P margin="0" fontSize="12px" textAlign="left" style={{ color: "#666" }}>
        This is the long article on blog.healthy-pet.app. App slides above stay
        separate. The public URL uses the English slug
        {slug ? `: /blog/${slug}` : " (fill in the EN slug above)."}.
      </P>

      <Div width="100%" gap="8px" alignStart>
        <P fontSize="14px" margin="0" textAlign="left">
          Status
        </P>
        <select
          value={site.status}
          onChange={(e) => patchSite({ status: e.target.value })}
          style={{ padding: "6px 8px", border: "1px solid #d9d9d9", borderRadius: 6 }}
        >
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
      </Div>

      {siteReady ? (
        <AdminArticleSiteEditor
          key={articleFirestoreKey || "new"}
          initialHtml={site.bodyHtml}
          articleId={articleFirestoreKey}
          onChange={(bodyHtml) => patchSite({ bodyHtml })}
        />
      ) : (
        <P margin="0" fontSize="13px" textAlign="left" role={loadError ? "alert" : undefined}>
          {loadError || "Loading site article…"}
        </P>
      )}

      <Div width="100%">
        <P fontSize="14px" margin="0 0 5px 0" textAlign="left">
          Excerpt
        </P>
        <FeedbackFormTextarea
          as="textarea"
          rows={3}
          value={site.excerpt}
          onChange={(e) => patchSite({ excerpt: e.target.value })}
          placeholder="Short summary for the blog list and search results"
          style={{
            padding: "8px 5px",
            border: "1px gray solid",
            width: "100%",
            boxSizing: "border-box",
            resize: "vertical",
          }}
        />
      </Div>

      <Div width="100%">
        <P fontSize="14px" margin="0 0 5px 0" textAlign="left">
          SEO title
        </P>
        <FeedbackFormInput
          value={site.seoTitle}
          onChange={(e) => patchSite({ seoTitle: e.target.value })}
          placeholder="Defaults to the English title"
          style={{ padding: "3px 5px", border: "1px gray solid" }}
        />
      </Div>

      <Div width="100%">
        <P fontSize="14px" margin="0 0 5px 0" textAlign="left">
          SEO description
        </P>
        <FeedbackFormTextarea
          as="textarea"
          rows={3}
          value={site.seoDescription}
          onChange={(e) => patchSite({ seoDescription: e.target.value })}
          placeholder="Defaults to the excerpt or English subtitle"
          style={{
            padding: "8px 5px",
            border: "1px gray solid",
            width: "100%",
            boxSizing: "border-box",
            resize: "vertical",
          }}
        />
      </Div>

      <Div width="100%" gap="8px" alignStart>
        <P fontSize="14px" margin="0" textAlign="left">
          Social image
        </P>
        <P margin="0" fontSize="12px" textAlign="left" style={{ color: "#666" }}>
          Optional. Empty uses the cover image.
        </P>
        <input
          type="file"
          accept="image/*"
          disabled={!articleFirestoreKey || ogUploading}
          onChange={onOgPick}
        />
        {site.ogImageUrl && isValidArticleImageUrl(site.ogImageUrl) ? (
          <img
            src={site.ogImageUrl}
            alt=""
            style={{ maxWidth: 240, borderRadius: 8 }}
          />
        ) : null}
        {site.ogImageUrl ? (
          <Button type="button" width="auto" outlineButton onClick={onOgClear}>
            Remove social image
          </Button>
        ) : null}
      </Div>

      <label>
        <input
          type="checkbox"
          checked={Boolean(site.noindex)}
          onChange={(e) => patchSite({ noindex: e.target.checked })}
        />{" "}
        Hide this page from search engines
      </label>
    </Div>
  );
}
